/**
 * Status Values Constants
 * 
 * This file defines all status values used throughout the application.
 * All status values follow the standard: First letter capitalized, rest lowercase.
 * 
 * IMPORTANT: Always use these constants instead of string literals to ensure consistency.
 */

/**
 * Load Status Values
 * Used in the `status` field of loads collection
 */
export const LOAD_STATUS = {
  ACTIVE: 'Active',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  PENDING: 'Pending'
} as const;

/**
 * Shipping Schedule Status Values
 * Used in the `shippingScheduleStatus` field of purchase orders
 */
export const SHIPPING_SCHEDULE_STATUS = {
  ACTIVE: 'Active',
  IN_PROGRESS: 'In Progress',
  CARRIER_PENDING: 'Carrier Pending',
  OPEN: 'Open',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
} as const;

/**
 * Purchase Order Status Values
 * Used in the `status` field of purchase orders
 */
export const PO_STATUS = {
  PROCESSING: 'Processing',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  OPEN: 'Open',
  DELETED: 'Deleted'
} as const;

/**
 * Subscription Status Constants
 * Used in the `status` field of subscriptions collection
 */
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
  PAST_DUE: 'Past Due',
  TRIAL: 'Trial',
  EXPIRED: 'Expired'
} as const;

/**
 * Payment Status Constants
 * Used in the `status` field of payments collection
 */
export const PAYMENT_STATUS = {
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
  PENDING: 'Pending',
  REFUNDED: 'Refunded'
} as const;

/**
 * Cancellation Workflow Status
 * Used for two-party cancellation (shipper/broker request → carrier approve/reject).
 * Stored on loads and optionally on purchase orders; do not reuse LOAD_STATUS/PO_STATUS.
 * For account/company cancellation (Tranzit admin flows), use accountCancellationWorkflow.ts — not these values.
 */
export const CANCELLATION_STATUS = {
  NONE: 'none',
  REQUESTED: 'requested',
  PENDING_CARRIER_RESPONSE: 'pending_carrier_response',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  COMPLETED_WITH_TONU: 'completed_with_tonu',
  COMPLETED_WITHOUT_TONU: 'completed_without_tonu'
} as const;

/**
 * Type exports for TypeScript type safety
 */
export type LoadStatus = typeof LOAD_STATUS[keyof typeof LOAD_STATUS];
export type ShippingScheduleStatus = typeof SHIPPING_SCHEDULE_STATUS[keyof typeof SHIPPING_SCHEDULE_STATUS];
export type POStatus = typeof PO_STATUS[keyof typeof PO_STATUS];
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUS[keyof typeof SUBSCRIPTION_STATUS];
export type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];
export type CancellationStatus = typeof CANCELLATION_STATUS[keyof typeof CANCELLATION_STATUS];
